<template>
  <div v-if="manageAccess.readTxns">
    <ic-details v-if="getterPopupName === 'VaIcDetails'"
              :txnDetails="popupDetails" @refreshList="refreshDetails">
      <template v-slot:actions>
        <div class="extra-actions">
          <md-field id="RefundAction" v-if="manageAccess.manageTxns && refundOptions.length">
            <md-select @md-opened="setDropdownPosOffset('RefundAction')"
                      v-model="refundAction" placeholder="Refund Action">
              <md-option v-for="(action, index) in refundOptions" :key="index"
                        :value="action.value">
                {{action.text}}
              </md-option>
            </md-select>
          </md-field>

          <button class="primary-btn" v-if="webhooksAllowed" @click="resendWebhook">
            Resend Webhook
            <i class="fa fa-paper-plane" />
          </button>
        </div>
      </template>
    </ic-details>

    <ic-filter v-else-if="getterPopupName === 'VaIcFilter'"
              @updateFilters="updateFilters" />
    <ic-export v-else-if="getterPopupName === 'VaIcExport'"
              :exportOptions="paginate" :exportRecords="exportRecords" />
    <settle-new v-else-if="getterPopupName === 'SettleNew'" :id="popupDetails.id"
              @refreshList="refreshList" />
    <refund-confirmation v-else-if="getterPopupName === 'RefundConfirmation'"
              @refreshList="refreshList" :refundAction="refundAction"
              @openTxns="showDetails" :id="popupDetails.id" />
    <refunds-list v-else-if="getterPopupName === 'RefundsList'"
              :list="refundsList" />

    <div class="table-title">
      <div class="subject">
        <p>Invalid Credits</p>
        <span v-if="typeof paginate.total === 'number'">
          Total ({{paginate.total}})
        </span>

        <ic-webhooks v-if="paginate.total && webhooksAllowed && !searchTerm"
              :paginate="paginate" @resendWebhooks="bulkResendWebhooks" />
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('VaIcExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button :class="{'filters-applied': filtersApplied}"
                  @click="openPopup('VaIcFilter')"
                  class="dark-btn">
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <ic-search placeholder="Search ID / Bank ref number / VA number"
                :searchTerm="searchTerm"
                :triggerSearch="(init) => triggerSearchPaginate(false, init)"
                @populateList="clearSearch"
                @setFunctions="setSearchFunctions" />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p>ID</p>
      <p class="d-l">Time</p>
      <p>Payment Mode</p>
      <p class="d-xl" v-if="!vaId">Virtual Account Number</p>
      <p class="d-l">Bank Ref Num</p>
      <p>Transaction Amount</p>
      <p class="d-l">Refund Status</p>
      <p></p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(txn, index) in list" :key="index"
        @click="showDetails(txn)"
      >
        <p>
          <b v-if="!isDesktop()">ID:</b>
          <span>{{txn.id}}</span>
        </p>
        <p class="mt-grey-text d-l">
          {{txn.created_at}}
        </p>
        <p>
          <b v-if="!isDesktop()">Pymt Mode:</b>
          {{txn.payment_type}}
        </p>
        <p class="d-xl" v-if="!vaId">
          <b v-if="!isDesktop()">VA Num:</b>
          <span>{{txn.bene_account_no}}</span>
        </p>
        <p class="d-l">
          <b v-if="!isDesktop()">Bank Ref Num:</b>
          <span>{{txn.bank_ref_num}}</span>
        </p>
        <p>
          <b v-if="!isDesktop()">Txn Amt:</b>
          {{amountFormat(txn.amount)}}
        </p>
        <p class="mt-grey-text d-l">
          <b v-if="!isDesktop()">Status:</b>
          {{txn.refund_status}}
          <i v-if="txn.refund_status === 'PENDING'" class="fa fa-sync-alt"
              @click="refreshDetails($event, txn)" />
        </p>
        <p v-if="txn.refund_id" class="download" @click="viewRefunds($event, txn.id)">
          Refunds
        </p>
        <p v-else></p>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || list.length < paginate.total" />

    <div class="table-empty" v-else-if="paginate.total === 0">
      <p v-if="searchTerm">Unable to find invalid credits with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / Bank Reference Number / Virtual Account Number.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>
  </div>
</template>

<script>
import RefundsList from '@/components/global/ExpandCollapseList'
import RefundConfirmation from '@/components/global/InvalidCredits/RefundConfirmation'
import IcWebhooks from '@/components/global/BulkResendWebhooks'
import IcSearch from '@/components/global/SearchRecords'
import IcExport from '@/components/global/ExportConfirmation'
import IcDetails from '@/components/AccountStatement/StatementDetails'
import IcFilter from '@/components/VirtualAccounts/InvalidCredits/Filter'
import SettleNew from '@/components/Bank/SettleNew'

import ApiServices from '@/services/api/vaInvalidCredits'
import listMixins from '@/mixins/listMixins'
import { amountFormat, setDropdownPosOffset } from '@/utils/helpers'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'InvalidCredits',
  components: { IcDetails, IcFilter, IcSearch, IcExport, SettleNew, RefundConfirmation, RefundsList, IcWebhooks },
  mixins: [listMixins],
  data () {
    return {
      refundOptions: [],
      refundObj: {
        new: {
          text: 'Settle to New',
          value: 'SETTLE_TO_NEW'
        },
        hypto: {
          text: 'Settle to Hypto',
          value: 'SETTLE_TO_HYPTO'
        },
        retry: {
          text: 'Retry Refund',
          value: 'RETRY_REFUND'
        }
      },
      refundAction: null,
      manageAccess: {
        readTxns: true
      },
      refundsList: []
    }
  },
  props: {
    vaId: [String, Boolean, Number]
  },
  computed: {
    ...mapGetters(['getterSectionDetails'])
  },
  watch: {
    refundAction (newVal) {
      if (newVal === 'SETTLE_TO_NEW') {
        this.openPopup('SettleNew')
      } else if (newVal !== null) {
        this.openPopup('RefundConfirmation')
      }
    }
  },
  created () {
    this.startProcessing()
  },
  methods: {
    amountFormat,
    setDropdownPosOffset,
    ...mapMutations(['SET_UNPRIVILEGE_PAGE', 'SET_VA_CONNECTED_BANKING']),
    populateParams () {
      this.api['export'] = ApiServices.export
      this.api['bulkResendWebhook'] = ApiServices.bulkResendWebhook
      this.api['fetchStatus'] = ApiServices.refundStatus
      this.api['resendWebhook'] = ApiServices.resendWebhook
      this.api['search'] = ApiServices.search
      this.keys['list'] = 'invalid_credits'
      this.keys['popup'] = 'VaIcDetails'
      this.paginate['type'] = 'invalid credit'
      if (this.vaId) {
        this.params['search'] = {
          hypto_va_id: this.vaId
        }
      }

      if (this.vaId) {
        this.setHighlightSelectors([1, 4])
      } else {
        this.setHighlightSelectors([1, 4, 5])
      }
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        if (start) {
          params['offset'] = start
          params['filter_token'] = this.paginate['filter_token']
        } else if (this.vaId) {
          params['hypto_va_id'] = this.vaId
          params['fetch_va_details'] = !this.getterSectionDetails
        }

        ApiServices.invalidCredits(params).then(response => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          let data = response.data
          if (data) {
            this.setAccess(response.privileges)

            if (start) {
              this.list.push(...data.invalid_credits)
            } else {
              this.list = data.invalid_credits
              this.setPaginate(data)
              this.populateMeta(response)
            }
          } else {
            this.SET_UNPRIVILEGE_PAGE(this.vaId ? 'Virtual Account Details' : 'Invalid Credits')
          }
          resolve()
        }).catch((error) => {
          if (error.status === 404) {
            this.$router.push({name: 'VAList'})
          }
        })
      })
    },
    populateMeta ({data, privileges}, fromSearch) {
      if (!this.getterSectionDetails) {
        this.SET_SECTION_DETAILS({
          va: data.virtual_account,
          privileges: privileges,
          wl_limit: data.whitelist_rmtr_limit,
          self_va_enabled: data.self_va_enabled
        })
      }

      this.webhooksAllowed = data.webhook_allowed
      this.SET_VA_CONNECTED_BANKING(data.connected_banking_enabled)
    },
    setAccess (privileges) {
      this.manageAccess = {
        readTxns: privileges.received_va_txns,
        manageTxns: privileges.manage_va_txns
      }
      if (!this.manageAccess.readTxns) {
        this.SET_UNPRIVILEGE_PAGE(' ')
      }
    },
    showDetails (txn) {
      this.refundAction = null
      this.openPopup(this.keys.popup)
      this.$router.replace({query: {q: encodeURIComponent(txn.id)}})

      if (txn) {
        this.popupDetails = txn
        this.refundOptions = []

        switch (txn.refund_status) {
          case 'FAILED':
            this.refundOptions.push(this.refundObj.new, this.refundObj.hypto, this.refundObj.retry)
            break
          case 'NEED_REMITTER_DETAILS':
            this.refundOptions.push(this.refundObj.new, this.refundObj.hypto)
            break
        }
      }
    },
    fetchSearchRecords (params, start, initial) {
      return new Promise(resolve => {
        this.api.search(params).then((response) => {
          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data[this.keys.list])
              this.postSearch(false, this.paginate.total)
            } else {
              this.list = data[this.keys.list] || []
              this.paginate['start'] = 0
              this.paginate['total'] = data.total
              this.paginate['page_size'] = data.page_size
              this.paginate['filter_token'] = data.filter_token
              this.paginate['data_added'] = true

              this.postSearch(initial, this.paginate.total)
              if (initial) {
                this.populateMeta(response, true)
              }
            }
            resolve()
          } else {
            let unprivilegePanel = 'Invalid Credits'
            if (this.vaId) {
              if (initial && !response.data) {
                unprivilegePanel = 'Virtual Account Details'
              } else {
                unprivilegePanel = ' '
              }
            }
            this.SET_UNPRIVILEGE_PAGE(unprivilegePanel)
          }
        }).catch(() => {
          this.list = []
          this.paginate['total'] = 0
        })
      })
    },
    viewRefunds (event, id) {
      event.stopPropagation()
      ApiServices.refunds(id).then(response => {
        if (response.success) {
          this.refundsList = response.data.refunds
          this.openPopup('RefundsList')
        } else {
          this.SET_UNPRIVILEGE_PAGE(this.vaId ? ' ' : 'Invalid Credits')
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/table.scss';

  .extra-actions {
    text-align: left;

    .md-field {
      margin: 0 10px 10px 0 !important;
      width: 48%;
    }

    button {
      margin-bottom: 10px;
    }
    i {
      margin-left: 10px;
    }
  }

  .desktop {
    .extra-actions {
      @include custom-flex(null);

      button {
        min-width: 170px;
      }
    }

    .table-row {
      p {
        word-break: break-word;
      }
    }
  }

  .tablet, .mobile {
    .extra-actions {
      @include custom-flex(space-between, null);

      button {
        flex: 1;
        font-size: 12px;
        padding: 8px 5px !important;
      }
    }

    .table-row {
      grid-template-areas: "id created_time"
                            "field_4 pymt_mode"
                            "field_6 field_5"
                            "field_7 field_7"
                            "field_8 field_8";

      p {
        white-space: unset !important;

        &:nth-child(1) {
          grid-area: id;
        }
        &:nth-child(2) {
          grid-area: created_time;
          text-align: right;
        }
        &:nth-child(3) {
          grid-area: pymt_mode;
          text-align: right;
        }
        &:nth-child(4) {
          grid-area: field_4;
        }
        &:nth-child(5) {
          grid-area: field_5;
          text-align: right;
        }
        &:nth-child(6) {
          grid-area: field_6;
        }
        &:nth-child(7) {
          grid-area: field_7;
        }
        &:nth-child(8) {
          grid-area: field_8;
        }
      }
    }
  }
</style>
